
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "./services/auth/authContext";
import { useEffect } from "react";
import * as ga from "./utils/googleAnalytics";
import ProtectedRoute from "./components/ProtectedRoute";
import UniversalProtectedRoute from "./components/UniversalProtectedRoute";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import RestaurantLogin from "./pages/RestaurantLogin";
import RestaurantSignup from "./pages/RestaurantSignup";
import RestaurantOnboarding from "./pages/RestaurantOnboarding";
import LocationSetup from "./pages/LocationSetup";
import ExistingRestaurantRedirect from "./pages/ExistingRestaurantRedirect";
import RestaurantMenuUpload from "./pages/RestaurantMenuUpload";
import RestaurantMenuUploadFallback from "./pages/RestaurantMenuUploadFallback";
import RestaurantMenuClaim from "./pages/RestaurantMenuClaim";
import RestaurantMenuProcessingAsync from "./pages/RestaurantMenuProcessingAsync";
import AuthMenuProcessing from "./pages/AuthMenuProcessing";
import MenuExtractionLimitReached from "./pages/MenuExtractionLimitReached";
// import RestaurantMenuSections from "./pages/RestaurantMenuSections"; // REMOVED: Redundant - RestaurantMenuReview handles section management
import RestaurantMenuReview from "./pages/RestaurantMenuReview";
import MenuTableEditor from "./pages/MenuTableEditor";
import RestaurantMenuPreview from "./pages/RestaurantMenuPreview";
import RestaurantMenuPublish from "./pages/RestaurantMenuPublish";
import MenuPreview from "./pages/MenuPreview";
import RestaurantDashboard from "./pages/RestaurantDashboard";
import RestaurantDashboardWithSubscription from "./pages/RestaurantDashboardWithSubscription";
import DashboardRouter from "./pages/DashboardRouter";
import AIImagePurchaseHistory from "./pages/AIImagePurchaseHistory";
import ProductSelection from "./pages/ProductSelection";
import PlanSelection from "./pages/PlanSelection";
import PaymentSetup from "./pages/PaymentSetup";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentSuccessSales from "./pages/PaymentSuccessSales";
import PaymentCancelled from "./pages/PaymentCancelled";
import ActivationLanding from "./pages/ActivationLanding";
import AboutUs from "./pages/AboutUs";
import Pricing from "./pages/Pricing";
import Customers from "./pages/Customers";
import RestaurantStaff from "./pages/RestaurantStaff";
import CustomerBrowse from "./pages/CustomerBrowse";
import RestaurantSettings from "./pages/RestaurantSettings";
import AIHostSettingsFullPage from "./pages/virtual-host/AIHostSettingsFullPage";
import AnalyticsV2 from "./pages/AnalyticsV2";
import DemoLoginV3 from "./pages/DemoLoginV3";
import SeasonalMenuCreate from "./pages/SeasonalMenuCreate";
import SeasonalMenuEdit from "./pages/SeasonalMenuEdit";
import SeasonalMenuUpload from "./pages/SeasonalMenuUpload";
import SeasonalMenuItems from "./pages/SeasonalMenuItems";
import SeasonalMenuCopy from "./pages/SeasonalMenuCopy";
import SeasonalMenuCopyFromMain from "./pages/SeasonalMenuCopyFromMain";
import SeasonalMenuProcessing from "./pages/SeasonalMenuProcessing";
import HeroCarouselEdit from "./pages/HeroCarouselEdit";
import OAuthCallback from "./pages/OAuthCallback";
import AdminAccess from "./pages/AdminAccess";
import CustomerPreferenceInsights from "./pages/CustomerPreferenceInsights";
import WaiterInviteAccept from "./pages/WaiterInviteAccept";
import { Navigate } from "react-router-dom";
import Restaurants from "./pages/Restaurants";
import RestaurantMenuSearch from "./pages/RestaurantMenuSearch";
import MenuSearchResults from "./pages/MenuSearchResults";
import Error404 from "./pages/Error404";
import CaseStudies from "./pages/CaseStudies";
import CaseStudyDetail from "./pages/CaseStudyDetail";
import Enterprise from "./pages/Enterprise";
import ContactUs from "./pages/ContactUs";
import Blog from "./pages/Blog";
import Error403 from "./pages/Error403";
import ChatWidget from "./components/ChatWidget";
import CookieConsent from "./components/CookieConsent";
import Error500 from "./pages/Error500";
import Error503 from "./pages/Error503";
import Terms from "./pages/Terms";
import VirtualHostTerms from "./pages/VirtualHostTerms";
import Privacy from "./pages/Privacy";
import Cookies from "./pages/Cookies";
import Accessibility from "./pages/Accessibility";
import FAQ from "./pages/FAQ";
import ScrollToTop from "./components/ScrollToTop";
import AdminLogin from "./pages/admin/AdminLogin";
import BlogManagement from "./pages/admin/BlogManagement";
import AssetManagement from "./pages/admin/AssetManagement";
import BlogDetail from "./pages/BlogDetail";
import AppDownload from "./pages/AppDownload";
import StaffAppDownload from "./pages/StaffAppDownload";
import DeleteAccount from "./pages/DeleteAccount";
import VirtualAssistant from "./pages/products/VirtualAssistant";
import VirtualHost from "./pages/products/VirtualHost";
import VirtualHostSetup from "./pages/virtual-host/VirtualHostSetup";
import StripeConnectSuccess from "./pages/virtual-host/StripeConnectSuccess";
import StripeConnectRefresh from "./pages/virtual-host/StripeConnectRefresh";
import VirtualHostOrderSuccess from "./pages/VirtualHostOrderSuccess";
import VirtualHostCallLog from "./pages/VirtualHostCallLog";
import MenuBrowser from "./pages/MenuBrowser";
import { SalesAuthProvider } from "./contexts/SalesAuthContext";
import SalesLogin from "./pages/SalesLogin";
import ProtectedSalesRoute from "./components/ProtectedSalesRoute";
import SalesLayout from "./components/sales/SalesLayout";
import SalesDashboard from "./pages/SalesDashboard";
import SalesSessionsList from "./pages/SalesSessionsList";
import SalesUpload from "./pages/SalesUpload";
import SalesUserManagement from "./pages/SalesUserManagement";
import CallAnalyzer from "./pages/CallAnalyzer";
import SalesChangePassword from "./pages/SalesChangePassword";
import SalesUploadHistory from "./pages/SalesUploadHistory";
import VHStaffLogin from "./pages/VHStaffLogin";
import VHStaffLoginBranded from "./pages/VHStaffLoginBranded";
import VHStaffAcceptInvitation from "./pages/VHStaffAcceptInvitation";
import VHStaffForgotPassword from "./pages/VHStaffForgotPassword";
import VHStaffResetPassword from "./pages/VHStaffResetPassword";
import VHStaffDashboard from "./pages/VHStaffDashboard";
import VHStaffProtectedRoute from "./components/VHStaffProtectedRoute";

const queryClient = new QueryClient();

// Component to track route changes
function RouteTracker() {
  const location = useLocation();
  
  useEffect(() => {
    ga.pageview(location.pathname + location.search);
  }, [location]);
  
  return null;
}

// Add routes for all the pages we'll need
const App = () => {
  useEffect(() => {
    // Initialize GA on app load
    ga.initGA();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthProvider>
            <ScrollToTop />
            <RouteTracker />
            <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/restaurants" element={<Restaurants />} />
          <Route path="/enterprise" element={<Enterprise />} />
          <Route path="/pricing" element={<Navigate to="/products/virtual-assistant" replace />} />
          <Route path="/products/virtual-assistant" element={<VirtualAssistant />} />
          <Route path="/products/virtual-host" element={<VirtualHost />} />
          <Route path="/virtual-host/setup" element={<VirtualHostSetup />} />
          <Route path="/virtual-host/stripe-connect/success" element={<StripeConnectSuccess />} />
          <Route path="/virtual-host/stripe-connect/refresh" element={<StripeConnectRefresh />} />
          {/* VH Staff Routes */}
          <Route path="/vh-staff-login" element={<VHStaffLogin />} />
          <Route path="/vh-staff-dashboard" element={<VHStaffProtectedRoute><VHStaffDashboard /></VHStaffProtectedRoute>} />
          <Route path="/vh-staff-accept" element={<VHStaffAcceptInvitation />} />
          {/* Branded VH Staff Routes (per-restaurant login with logo) */}
          <Route path="/staff/:slug" element={<VHStaffLoginBranded />} />
          <Route path="/staff/:slug/accept" element={<VHStaffAcceptInvitation />} />
          <Route path="/staff/:slug/forgot-password" element={<VHStaffForgotPassword />} />
          <Route path="/staff/:slug/reset-password" element={<VHStaffResetPassword />} />
          <Route path="/menu-intelligence" element={<Index />} />
          <Route path="/about" element={<AboutUs />} />
          <Route path="/customers" element={<Customers />} />
          <Route path="/restaurant-staff" element={<RestaurantStaff />} />
          <Route path="/app" element={<AppDownload />} />
          <Route path="/staff-app" element={<StaffAppDownload />} />
          <Route path="/delete-account" element={<DeleteAccount />} />
          <Route path="/contact" element={<ContactUs />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogDetail />} />
          <Route path="/login" element={<Index />} />
          <Route path="/signup" element={<Index />} />
          <Route path="/restaurant-login" element={<RestaurantLogin />} />
          <Route path="/restaurant-signup" element={<RestaurantSignup />} />
          <Route path="/restaurant-menu-search" element={<RestaurantMenuSearch />} />
          <Route path="/restaurant-menu-results" element={<MenuSearchResults />} />
          <Route path="/restaurant-setup" element={<ProtectedRoute><RestaurantOnboarding /></ProtectedRoute>} />
          <Route path="/location-setup" element={<ProtectedRoute><LocationSetup /></ProtectedRoute>} />
          <Route path="/existing-restaurant" element={<ProtectedRoute><ExistingRestaurantRedirect /></ProtectedRoute>} />
          <Route path="/restaurant-menu-upload" element={<RestaurantMenuUpload />} />
          <Route path="/restaurant-menu-upload-fallback" element={<RestaurantMenuUploadFallback />} />
          <Route path="/restaurant-menu-claim" element={<ProtectedRoute><RestaurantMenuClaim /></ProtectedRoute>} />
          <Route path="/restaurant-menu-processing" element={<RestaurantMenuProcessingAsync />} />
          <Route path="/auth-menu-processing" element={<AuthMenuProcessing />} />
          <Route path="/menu-extraction-limit" element={<MenuExtractionLimitReached />} />
          {/* <Route path="/restaurant-menu-sections" element={<ProtectedRoute><RestaurantMenuSections /></ProtectedRoute>} /> */} {/* REMOVED: Redundant - Direct workflow to review */}
          <Route path="/restaurant-menu-review" element={<UniversalProtectedRoute><RestaurantMenuReview /></UniversalProtectedRoute>} />
          <Route path="/menu-table-editor" element={<UniversalProtectedRoute><MenuTableEditor /></UniversalProtectedRoute>} />
          <Route path="/restaurant-menu-preview" element={<RestaurantMenuPreview />} />
          <Route path="/restaurant-menu-publish" element={<ProtectedRoute><RestaurantMenuPublish /></ProtectedRoute>} />
          <Route path="/menu-preview" element={<UniversalProtectedRoute><MenuPreview /></UniversalProtectedRoute>} />
          <Route path="/restaurant-dashboard" element={<UniversalProtectedRoute><DashboardRouter /></UniversalProtectedRoute>} />
          <Route path="/restaurant-dashboard-subscription" element={<UniversalProtectedRoute><RestaurantDashboardWithSubscription /></UniversalProtectedRoute>} />
          <Route path="/analytics" element={<UniversalProtectedRoute><AnalyticsV2 /></UniversalProtectedRoute>} />
          <Route path="/analytics-v2" element={<UniversalProtectedRoute><AnalyticsV2 /></UniversalProtectedRoute>} />
          <Route path="/restaurant-settings" element={<UniversalProtectedRoute><RestaurantSettings /></UniversalProtectedRoute>} />
          <Route path="/virtual-host/settings" element={<UniversalProtectedRoute><AIHostSettingsFullPage /></UniversalProtectedRoute>} />
          <Route path="/activate/:token" element={<ActivationLanding />} />
          <Route path="/product-selection" element={<ProductSelection />} />
          <Route path="/plan-selection" element={<PlanSelection />} />
          <Route path="/payment-setup" element={<PaymentSetup />} />
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/payment-success-sales" element={<PaymentSuccessSales />} />
          <Route path="/payment-cancelled" element={<PaymentCancelled />} />
          <Route path="/order/:orderId/success" element={<VirtualHostOrderSuccess />} />
          <Route path="/call-log" element={<UniversalProtectedRoute><VirtualHostCallLog /></UniversalProtectedRoute>} />
          <Route path="/m/:sessionId" element={<MenuBrowser />} />
          <Route path="/menu/:sessionId" element={<MenuBrowser />} />
          <Route path="/browse" element={<CustomerBrowse />} />
          <Route path="/dancing-queen" element={<DemoLoginV3 />} />
          {/* Demo V3 Routes - With unified session management */}
          <Route path="/demo/v3/login" element={<DemoLoginV3 />} />
          <Route path="/demo-login-v3" element={<DemoLoginV3 />} />
          <Route path="/seasonal-menu/create" element={<UniversalProtectedRoute><SeasonalMenuCreate /></UniversalProtectedRoute>} />
          <Route path="/seasonal-menu/:id/edit" element={<UniversalProtectedRoute><SeasonalMenuEdit /></UniversalProtectedRoute>} />
          <Route path="/seasonal-menu/:id/upload" element={<UniversalProtectedRoute><SeasonalMenuUpload /></UniversalProtectedRoute>} />
          <Route path="/seasonal-menu-processing" element={<UniversalProtectedRoute><SeasonalMenuProcessing /></UniversalProtectedRoute>} />
          <Route path="/seasonal-menu/:id/items" element={<UniversalProtectedRoute><SeasonalMenuItems /></UniversalProtectedRoute>} />
          <Route path="/seasonal-menu/:id/copy" element={<UniversalProtectedRoute><SeasonalMenuCopy /></UniversalProtectedRoute>} />
          <Route path="/seasonal-menu/:id/copy-from-main" element={<UniversalProtectedRoute><SeasonalMenuCopyFromMain /></UniversalProtectedRoute>} />
          <Route path="/hero-carousel/edit" element={<UniversalProtectedRoute><HeroCarouselEdit /></UniversalProtectedRoute>} />
          <Route path="/oauth/callback" element={<OAuthCallback />} />
          <Route path="/admin-access" element={<AdminAccess />} />
          <Route path="/customer-insights" element={<UniversalProtectedRoute><CustomerPreferenceInsights /></UniversalProtectedRoute>} />
          <Route path="/ai-image-purchases" element={<UniversalProtectedRoute><AIImagePurchaseHistory /></UniversalProtectedRoute>} />
          <Route path="/waiter/invite" element={<WaiterInviteAccept />} />
          {/* Case Studies */}
          <Route path="/case-studies" element={<CaseStudies />} />
          <Route path="/case-studies/:slug" element={<CaseStudyDetail />} />
          {/* Admin Pages */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/blog" element={<BlogManagement />} />
          <Route path="/admin/assets" element={<AssetManagement />} />
          {/* Sales Portal - Separate auth system */}
          <Route path="/sales-horse" element={<SalesAuthProvider><SalesLogin /></SalesAuthProvider>} />
          <Route path="/sales-horse/change-password" element={<SalesAuthProvider><SalesChangePassword /></SalesAuthProvider>} />
          <Route
            path="/sales-horse/dashboard"
            element={
              <SalesAuthProvider>
                <ProtectedSalesRoute>
                  <SalesLayout>
                    <SalesDashboard />
                  </SalesLayout>
                </ProtectedSalesRoute>
              </SalesAuthProvider>
            }
          />
          <Route
            path="/sales-horse/sessions"
            element={
              <SalesAuthProvider>
                <ProtectedSalesRoute>
                  <SalesLayout>
                    <SalesSessionsList />
                  </SalesLayout>
                </ProtectedSalesRoute>
              </SalesAuthProvider>
            }
          />
          <Route
            path="/sales-horse/upload-history"
            element={
              <SalesAuthProvider>
                <ProtectedSalesRoute>
                  <SalesLayout>
                    <SalesUploadHistory />
                  </SalesLayout>
                </ProtectedSalesRoute>
              </SalesAuthProvider>
            }
          />
          <Route
            path="/sales-horse/upload"
            element={
              <SalesAuthProvider>
                <ProtectedSalesRoute>
                  <SalesLayout>
                    <SalesUpload />
                  </SalesLayout>
                </ProtectedSalesRoute>
              </SalesAuthProvider>
            }
          />
          <Route
            path="/sales-horse/admin/users"
            element={
              <SalesAuthProvider>
                <ProtectedSalesRoute requireAdmin={true}>
                  <SalesLayout>
                    <SalesUserManagement />
                  </SalesLayout>
                </ProtectedSalesRoute>
              </SalesAuthProvider>
            }
          />
          <Route
            path="/sales-horse/admin/call-analyzer"
            element={
              <SalesAuthProvider>
                <ProtectedSalesRoute requireAdmin={true}>
                  <SalesLayout>
                    <CallAnalyzer />
                  </SalesLayout>
                </ProtectedSalesRoute>
              </SalesAuthProvider>
            }
          />
          <Route
            path="/sales-horse/settings"
            element={
              <SalesAuthProvider>
                <ProtectedSalesRoute>
                  <SalesLayout>
                    <div className="text-center py-20">
                      <h1 className="text-3xl font-bold text-white">Settings</h1>
                      <p className="mt-4 text-white/60">Coming soon</p>
                    </div>
                  </SalesLayout>
                </ProtectedSalesRoute>
              </SalesAuthProvider>
            }
          />
          {/* Legal pages */}
          <Route path="/terms" element={<Terms />} />
          <Route path="/virtual-host-terms" element={<VirtualHostTerms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/cookies" element={<Cookies />} />
          <Route path="/accessibility" element={<Accessibility />} />
          <Route path="/faq" element={<FAQ />} />
          {/* Error pages */}
          <Route path="/403" element={<Error403 />} />
          <Route path="/500" element={<Error500 />} />
          <Route path="/503" element={<Error503 />} />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<Error404 />} />
        </Routes>
        
        {/* Chatbot Widget - appears on all pages */}
        <ChatWidget />
        
        {/* Cookie Consent - appears on first visit */}
        <CookieConsent />
        
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  );
};

export default App;
